import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { toPng } from 'html-to-image';
import { Link, useNavigate } from 'react-router-dom';

import type { LeagueMemberRecord, PublishedPortalSnapshotRecord } from '../api/types.js';
import type { QaCleanupScope } from '../api/client.js';
import { useAppState } from './AppState.js';
import { EmptyState, ErrorPanel, LoadingPanel, PageIntro, PanelHeader } from './components.js';
import { mapError } from './errors.js';
import {
  useInvalidateQueries,
  useLeagueMembers,
  useRequiredLeagueId,
  useRequiredSeasonId,
  useResolvedActiveSeason,
  useSyncLeagueFromRoute,
} from './hooks.js';
import { SeasonRosterWorkspaceSection } from './seasonRosterComponents.js';
import { formatDate, toDateInputValue, toIsoFromDateInput } from '../lib/formatting.js';
import { buildSeasonRosterWorkspace } from './seasonRosterWorkspace.js';
import { selectLatestCompletedPublishedWeek, selectNextPublishedWeek } from './portalSnapshotViews.js';
import { loadRuntimeEnv } from './runtimeEnv.js';
import { PortalWeeklyRecapExport } from './portalRecapExport.js';

export function AdminHomePage() {
  const leagueId = useRequiredLeagueId();
  const navigate = useNavigate();
  useSyncLeagueFromRoute(leagueId);
  const { client, refreshIdentity, resetSeasonSelection, setActiveLeague, setBanner } = useAppState();
  const { invalidate, queryClient } = useInvalidateQueries();
  const { season, seasonsQuery } = useResolvedActiveSeason(leagueId);
  const leagueQuery = useQuery({
    queryKey: ['league', leagueId],
    enabled: Boolean(client),
    queryFn: async () => (await client!.getLeague(leagueId)).league,
  });
  const portalQuery = useQuery({
    queryKey: ['leaguePortal', leagueId],
    enabled: Boolean(client),
    queryFn: async () => (await client!.getLeaguePortalStatus(leagueId)).portal,
  });
  const [name, setName] = useState('');
  const [portalPassword, setPortalPassword] = useState('');
  const [publishSeasonId, setPublishSeasonId] = useState('');
  const [selectedRecapWeekId, setSelectedRecapWeekId] = useState('');
  const [isExportingRecap, setIsExportingRecap] = useState(false);
  const [isRenderingRecapPreview, setIsRenderingRecapPreview] = useState(false);
  const [recapPreviewUrl, setRecapPreviewUrl] = useState<string | null>(null);
  const [cleanupScope, setCleanupScope] = useState<QaCleanupScope>('all_scores');
  const [cleanupConfirmation, setCleanupConfirmation] = useState('');
  const isQa = loadRuntimeEnv().DEPLOY_ENVIRONMENT === 'qa';
  const recapExportRef = useRef<HTMLDivElement | null>(null);

  const metadataMutation = useMutation({
    mutationFn: async () => {
      if (!client) {
        throw new Error('API client unavailable');
      }
      return client.updateLeague(leagueId, {
        name: name.trim(),
      });
    },
    onSuccess: async () => {
      setBanner('success', 'League updated.');
      await invalidate(['league', leagueId]);
      await refreshIdentity(true);
    },
    onError: (error: unknown) => setBanner('error', mapError(error)),
  });

  const passwordMutation = useMutation({
    mutationFn: async () => {
      if (!client) {
        throw new Error('API client unavailable');
      }
      return client.updateLeaguePortalPassword(leagueId, {
        password: portalPassword.trim(),
      });
    },
    onSuccess: async () => {
      setPortalPassword('');
      setBanner('success', 'Guest portal password updated.');
      await invalidate(['leaguePortal', leagueId]);
    },
    onError: (error: unknown) => setBanner('error', mapError(error)),
  });

  const publishMutation = useMutation({
    mutationFn: async () => {
      if (!client) {
        throw new Error('API client unavailable');
      }
      if (!publishSeasonId) {
        throw new Error('Choose a season to publish.');
      }
      return client.publishLeaguePortal(leagueId, {
        seasonId: publishSeasonId,
      });
    },
    onSuccess: async () => {
      setBanner('success', 'Guest portal published.');
      await invalidate(['leaguePortal', leagueId], ['leaguePortalSnapshot', leagueId]);
    },
    onError: (error: unknown) => setBanner('error', mapError(error)),
  });

  const unpublishMutation = useMutation({
    mutationFn: async () => {
      if (!client) {
        throw new Error('API client unavailable');
      }
      return client.unpublishLeaguePortal(leagueId);
    },
    onSuccess: async () => {
      setBanner('success', 'Guest portal unpublished.');
      await invalidate(['leaguePortal', leagueId], ['leaguePortalSnapshot', leagueId]);
    },
    onError: (error: unknown) => setBanner('error', mapError(error)),
  });

  const cleanupMutation = useMutation({
    mutationFn: async () => client!.cleanupQaLeague(leagueId, {
      scope: cleanupScope,
      confirmLeagueName: cleanupConfirmation,
    }),
    onSuccess: async ({ scope }) => {
      setCleanupConfirmation('');
      resetSeasonSelection(leagueId);
      if (scope === 'entire_league') {
        queryClient.clear();
        navigate('/leagues', { replace: true });
        setActiveLeague(null);
        await refreshIdentity(true);
      } else {
        await queryClient.invalidateQueries();
      }
      setBanner('success', 'QA league data deleted.');
    },
    onError: (error: unknown) => setBanner('error', mapError(error)),
  });

  const portalSnapshotQuery = useQuery({
    queryKey: ['leaguePortalSnapshot', leagueId],
    enabled: Boolean(client && portalQuery.data?.published),
    queryFn: async () => (await client!.getLeaguePortalSnapshot(leagueId)).snapshot,
  });

  useEffect(() => {
    if (leagueQuery.data) {
      setName(leagueQuery.data.name);
    }
  }, [leagueQuery.data]);

  useEffect(() => {
    if (publishSeasonId) {
      return;
    }
    if (season?.id) {
      setPublishSeasonId(season.id);
      return;
    }
    if (portalQuery.data?.publishedSeasonId) {
      setPublishSeasonId(portalQuery.data.publishedSeasonId);
    }
  }, [portalQuery.data?.publishedSeasonId, publishSeasonId, season?.id]);

  const publishedSnapshot = portalSnapshotQuery.data ?? null;
  const completedPublishedWeeks = useMemo(
    () => (publishedSnapshot ? publishedSnapshot.weeks.filter((week) => week.completedMatches > 0) : []),
    [publishedSnapshot],
  );
  const selectedRecapWeek =
    completedPublishedWeeks.find((week) => week.matchDayId === selectedRecapWeekId) ??
    (publishedSnapshot ? selectLatestCompletedPublishedWeek(publishedSnapshot) : null);
  const nextRecapWeek =
    publishedSnapshot && selectedRecapWeek
      ? selectNextPublishedWeek(publishedSnapshot, selectedRecapWeek.matchDayId)
      : null;

  useEffect(() => {
    if (!publishedSnapshot) {
      if (selectedRecapWeekId) {
        setSelectedRecapWeekId('');
      }
      return;
    }
    const latestCompletedWeek = selectLatestCompletedPublishedWeek(publishedSnapshot);
    if (!latestCompletedWeek) {
      if (selectedRecapWeekId) {
        setSelectedRecapWeekId('');
      }
      return;
    }
    if (!selectedRecapWeekId || !completedPublishedWeeks.some((week) => week.matchDayId === selectedRecapWeekId)) {
      setSelectedRecapWeekId(latestCompletedWeek.matchDayId);
    }
  }, [completedPublishedWeeks, publishedSnapshot, selectedRecapWeekId]);

  async function downloadWeeklyRecap(
    snapshot: PublishedPortalSnapshotRecord,
    selectedWeekId: string,
  ): Promise<void> {
    setIsExportingRecap(true);
    try {
      const dataUrl = recapPreviewUrl ?? (await renderWeeklyRecapImage());
      const selectedWeek = snapshot.weeks.find((week) => week.matchDayId === selectedWeekId) ?? null;
      const download = document.createElement('a');
      download.href = dataUrl;
      download.download = `${snapshot.league_name.toLowerCase().replace(/\s+/g, '-')}-week-${selectedWeek?.weekNumber ?? 'recap'}.png`;
      download.click();
    } finally {
      setIsExportingRecap(false);
    }
  }

  async function renderWeeklyRecapImage(): Promise<string> {
    if (!recapExportRef.current) {
      throw new Error('Weekly recap preview is unavailable.');
    }
    return toPng(recapExportRef.current, {
      backgroundColor: '#ffffff',
      cacheBust: true,
      pixelRatio: 3,
    });
  }

  useEffect(() => {
    if (!publishedSnapshot || !selectedRecapWeek) {
      setRecapPreviewUrl(null);
      return;
    }

    let cancelled = false;
    setIsRenderingRecapPreview(true);

    const renderPreview = async () => {
      try {
        await new Promise((resolve) => window.requestAnimationFrame(resolve));
        const dataUrl = await renderWeeklyRecapImage();
        if (!cancelled) {
          setRecapPreviewUrl(dataUrl);
        }
      } catch (error) {
        if (!cancelled) {
          setRecapPreviewUrl(null);
          setBanner('error', mapError(error));
        }
      } finally {
        if (!cancelled) {
          setIsRenderingRecapPreview(false);
        }
      }
    };

    void renderPreview();

    return () => {
      cancelled = true;
    };
  }, [nextRecapWeek, publishedSnapshot, selectedRecapWeek, setBanner]);

  if (leagueQuery.isLoading || portalQuery.isLoading || seasonsQuery.isLoading) {
    return <LoadingPanel title="League Settings" message="Loading league metadata." />;
  }
  if (leagueQuery.isError || portalQuery.isError || seasonsQuery.isError || !leagueQuery.data || !portalQuery.data) {
    return <ErrorPanel title="League Settings" message={mapError(leagueQuery.error)} />;
  }

  const availableSeasons = seasonsQuery.data ?? [];
  const portal = portalQuery.data;

  return (
    <>
      <PageIntro
        title="League Settings"
      />
      <section className="panel stack">
        <PanelHeader title="League Name" />
        <form
          className="stack"
          onSubmit={(event) => {
            event.preventDefault();
            void metadataMutation.mutateAsync();
          }}
        >
          <div className="field">
            <label>Name</label>
            <input required value={name} onChange={(event) => setName(event.target.value)} />
          </div>
          <div className="inline-actions">
            <button type="submit" disabled={metadataMutation.isPending}>
              Save Name
            </button>
          </div>
        </form>
      </section>
      <section className="grid">
        <article className="panel stack">
          <PanelHeader
            title="Published Member Portal"
          />
          <div className="card portal-route-card">
            <div className="stat-card-label">Guest Route</div>
            <div className="portal-route-value">{portal.guestUrl}</div>
          </div>
          <form
            className="stack"
            onSubmit={(event) => {
              event.preventDefault();
              void passwordMutation.mutateAsync();
            }}
          >
            <div className="field">
              <label htmlFor="portal-password">Shared Guest Password</label>
              <input
                id="portal-password"
                type="password"
                minLength={4}
                required
                placeholder="Minimum 4 characters"
                value={portalPassword}
                onChange={(event) => setPortalPassword(event.target.value)}
              />
            </div>
            <div className="inline-actions">
              <button type="submit" disabled={passwordMutation.isPending}>
                {passwordMutation.isPending ? 'Saving...' : portal.passwordConfigured ? 'Rotate Password' : 'Set Password'}
              </button>
            </div>
          </form>
          <form
            className="stack"
            onSubmit={(event) => {
              event.preventDefault();
              void publishMutation.mutateAsync();
            }}
          >
            <div className="field">
              <label htmlFor="portal-season">Season to Publish</label>
              <select
                id="portal-season"
                value={publishSeasonId}
                onChange={(event) => setPublishSeasonId(event.target.value)}
              >
                <option value="">Select season</option>
                {availableSeasons.map((availableSeason) => (
                  <option key={availableSeason.id} value={availableSeason.id}>
                    {availableSeason.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="inline-actions">
              <button
                type="submit"
                disabled={publishMutation.isPending || !portal.passwordConfigured || !publishSeasonId}
              >
                {publishMutation.isPending ? 'Publishing...' : 'Publish Portal'}
              </button>
              {portal.published ? (
                <button
                  className="secondary"
                  type="button"
                  disabled={unpublishMutation.isPending}
                  onClick={() => void unpublishMutation.mutateAsync()}
                >
                  {unpublishMutation.isPending ? 'Unpublishing...' : 'Unpublish'}
                </button>
              ) : null}
            </div>
          </form>
          <p className="muted">
            Guest access stays unlocked only for the current browser session after the shared password is entered.
          </p>
        </article>
      </section>
      <section className="panel stack">
        <PanelHeader
          title="Weekly Recap Image"
        />
        {!portal.published ? (
          <EmptyState
            title="Publish a snapshot first"
            message="Publish the current season snapshot above before generating a weekly recap image."
          />
        ) : portalSnapshotQuery.isLoading ? (
          <LoadingPanel title="Weekly Recap Image" message="Loading published snapshot." />
        ) : portalSnapshotQuery.isError ? (
          <div className="alert error">{mapError(portalSnapshotQuery.error)}</div>
        ) : !publishedSnapshot || completedPublishedWeeks.length === 0 || !selectedRecapWeek ? (
          <EmptyState
            title="No completed published weeks"
            message="Publish a season that already has scored weeks before generating a recap image."
          />
        ) : (
          <>
            <div className="field recap-week-field">
              <label htmlFor="weekly-recap-week">Published Week</label>
              <select
                id="weekly-recap-week"
                value={selectedRecapWeek.matchDayId}
                onChange={(event) => setSelectedRecapWeekId(event.target.value)}
              >
                {completedPublishedWeeks.map((week) => (
                  <option key={week.matchDayId} value={week.matchDayId}>
                    Week {week.weekNumber} - {formatDate(week.date)} - {week.courseName}
                  </option>
                ))}
              </select>
            </div>
            <div className="inline-actions">
              <button
                type="button"
                disabled={isExportingRecap || isRenderingRecapPreview}
                onClick={() => void downloadWeeklyRecap(publishedSnapshot, selectedRecapWeek.matchDayId)}
              >
                {isExportingRecap ? 'Rendering Image...' : 'Download Image'}
              </button>
            </div>
            <div className="recap-preview-shell">
              {isRenderingRecapPreview ? (
                <div className="recap-preview-status">Rendering image preview...</div>
              ) : recapPreviewUrl ? (
                <img
                  className="recap-preview-image"
                  src={recapPreviewUrl}
                  alt={`Weekly recap preview for week ${selectedRecapWeek.weekNumber}`}
                />
              ) : (
                <div className="recap-preview-status">Preview unavailable.</div>
              )}
            </div>
            <div className="recap-render-source" aria-hidden="true">
              <div ref={recapExportRef}>
                <PortalWeeklyRecapExport
                  snapshot={publishedSnapshot}
                  selectedWeek={selectedRecapWeek}
                  nextWeek={nextRecapWeek}
                />
              </div>
            </div>
          </>
        )}
      </section>
      {isQa && (
        <section className="panel stack">
          <PanelHeader title="QA Data Cleanup" />
          <p className="muted">Delete test data from this league. Related scores, matches, attendance, standings, and published snapshots are also cleared where needed.</p>
          <div className="field">
            <label htmlFor="qa-cleanup-scope">Delete</label>
            <select id="qa-cleanup-scope" value={cleanupScope} disabled={cleanupMutation.isPending}
              onChange={(event) => { setCleanupScope(event.target.value as QaCleanupScope); setCleanupConfirmation(''); }}>
              <option value="all_scores">All scores</option>
              <option value="all_courses">All courses</option>
              <option value="all_seasons">All seasons</option>
              <option value="all_season_players">All season players</option>
              <option value="all_league_players">All league players except me</option>
              <option value="entire_league">Entire league</option>
            </select>
          </div>
          <div className="field">
            <label htmlFor="qa-cleanup-confirm">Type {leagueQuery.data.name} to confirm</label>
            <input id="qa-cleanup-confirm" value={cleanupConfirmation} autoComplete="off"
              onChange={(event) => setCleanupConfirmation(event.target.value)} />
          </div>
          <div className="inline-actions">
            <button type="button" className="danger" disabled={cleanupMutation.isPending || cleanupConfirmation !== leagueQuery.data.name}
              onClick={() => void cleanupMutation.mutateAsync()}>
              Delete QA Data
            </button>
          </div>
        </section>
      )}
    </>
  );
}

export function AdminMembersPage() {
  const leagueId = useRequiredLeagueId();
  useSyncLeagueFromRoute(leagueId);
  const { client, player, setBanner } = useAppState();
  const { invalidate } = useInvalidateQueries();
  const membersQuery = useLeagueMembers(leagueId);
  const [newMember, setNewMember] = useState({
    email: '',
    player_id: '',
    name: '',
    role: 'player',
    startingHandicap: '',
  });

  const addMutation = useMutation({
    mutationFn: async () =>
      client!.addMember(leagueId, {
        ...(newMember.startingHandicap.trim() ? { startingHandicap: Number(newMember.startingHandicap) } : {}),
        role: newMember.role as 'admin' | 'player',
        ...(newMember.email.trim() ? { email: newMember.email.trim() } : {}),
        ...(newMember.player_id.trim() ? { player_id: newMember.player_id.trim() } : {}),
        ...(newMember.name.trim() ? { name: newMember.name.trim() } : {}),
      }),
    onSuccess: async () => {
      setBanner('success', 'Member added.');
      setNewMember({ email: '', player_id: '', name: '', role: 'player', startingHandicap: '' });
      await invalidate(['members', leagueId]);
    },
    onError: (error: unknown) => setBanner('error', mapError(error)),
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      playerId,
      role,
      startingHandicap,
    }: {
      playerId: string;
      role: 'admin' | 'player';
      startingHandicap?: number;
    }) => client!.updateMember(leagueId, playerId, { role, ...(startingHandicap !== undefined ? { startingHandicap } : {}) }),
    onSuccess: async () => {
      setBanner('success', 'Member updated.');
      await invalidate(['seasonPlayers', leagueId], ['profileHistory', leagueId], ['dashboardSnapshot', leagueId]);
    },
    onError: (error: unknown) => setBanner('error', mapError(error)),
    onSettled: async () => { await invalidate(['members', leagueId]); },
  });

  const removeMutation = useMutation({
    mutationFn: async (playerId: string) => client!.removeMember(leagueId, playerId),
    onSuccess: async () => {
      setBanner('success', 'Member removed.');
      await invalidate(['members', leagueId]);
    },
    onError: (error: unknown) => setBanner('error', mapError(error)),
  });

  if (membersQuery.isLoading) {
    return <LoadingPanel title="League Members" message="Loading league members." />;
  }
  if (membersQuery.isError || !membersQuery.data) {
    return <ErrorPanel title="League Members" message={mapError(membersQuery.error)} />;
  }

  return (
    <>
      <PageIntro title="League Members" />
      <section className="grid two">
        <article className="panel">
          <h3>Add Member</h3>
          <form
            className="stack"
            onSubmit={(event) => {
              event.preventDefault();
              void addMutation.mutateAsync();
            }}
          >
            <div className="row">
              <div className="field">
                <label>Email</label>
                <input
                  type="email"
                  placeholder="player@example.com"
                  value={newMember.email}
                  onChange={(event) => setNewMember((current) => ({ ...current, email: event.target.value }))}
                />
              </div>
              <div className="field">
                <label>Player ID (optional)</label>
                <input
                  placeholder="uuid"
                  value={newMember.player_id}
                  onChange={(event) => setNewMember((current) => ({ ...current, player_id: event.target.value }))}
                />
              </div>
            </div>
            <div className="row">
              <div className="field">
                <label>Name</label>
                <input
                  placeholder="Player Name"
                  value={newMember.name}
                  onChange={(event) => setNewMember((current) => ({ ...current, name: event.target.value }))}
                />
              </div>
              <div className="field">
                <label>Role</label>
                <select
                  value={newMember.role}
                  onChange={(event) => setNewMember((current) => ({ ...current, role: event.target.value }))}
                >
                  <option value="player">Player</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>
            <div className="field">
              <label htmlFor="member-starting-handicap">League starting handicap</label>
              <input id="member-starting-handicap" type="number" min={-10} max={27} step="0.1"
                value={newMember.startingHandicap} onChange={(event) => setNewMember((current) => ({ ...current, startingHandicap: event.target.value }))} />
              <p className="muted">Required for a new member without qualifying league history.</p>
            </div>
            <div className="inline-actions">
              <button type="submit" disabled={addMutation.isPending}>
                Add Member
              </button>
            </div>
          </form>
        </article>

        <article className="panel">
          <PanelHeader title={`Active Members (${membersQuery.data.length})`} />
          {membersQuery.data.length === 0 ? (
            <EmptyState title="No members yet" message="Add the first member to start building the league roster." />
          ) : (
            <div className="card-list">
              {membersQuery.data.map((member) => (
                <MemberCard
                  key={member.player_id}
                  member={member}
                  isSelf={member.player_id === player?.id}
                  disabled={updateMutation.isPending}
                  onSave={(role, startingHandicap) =>
                    updateMutation.mutateAsync({
                      playerId: member.player_id,
                      role,
                      ...(startingHandicap !== undefined ? { startingHandicap } : {}),
                    })
                  }
                  onRemove={() => void removeMutation.mutateAsync(member.player_id)}
                />
              ))}
            </div>
          )}
        </article>
      </section>
    </>
  );
}

export function MemberCard({
  member,
  onSave,
  onRemove,
  isSelf,
  disabled = false,
}: {
  member: LeagueMemberRecord;
  onSave: (role: 'admin' | 'player', startingHandicap?: number) => Promise<unknown>;
  onRemove: () => void;
  isSelf: boolean;
  disabled?: boolean;
}) {
  const [role, setRole] = useState(member.role);
  const [startingHandicap, setStartingHandicap] = useState(member.startingHandicap?.toFixed(1) ?? '');
  const canEditHandicap = member.canEditStartingHandicap === true;
  useEffect(() => {
    setRole(member.role);
    setStartingHandicap(member.startingHandicap?.toFixed(1) ?? '');
  }, [member.role, member.startingHandicap, member.canEditStartingHandicap]);

  return (
    <div className="card">
      <p>
        <strong>{member.player?.name || member.player_id}</strong> ({member.player?.email || 'no email'})
      </p>
      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          const changedHandicap = canEditHandicap && Number(startingHandicap) !== member.startingHandicap
            ? Number(startingHandicap) : undefined;
          void onSave(role, changedHandicap).catch(() => { /* The page mutation displays the error. */ });
        }}
      >
        <div className="field">
          <label>Role</label>
          <select disabled={disabled} value={role} onChange={(event) => setRole(event.target.value as 'admin' | 'player')}>
            <option value="player">Player</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        <div className="field">
          <label htmlFor={`starting-handicap-${member.player_id}`}>League starting handicap</label>
          <input id={`starting-handicap-${member.player_id}`} type="number" min={-10} max={27} step="0.1"
            required={canEditHandicap} disabled={disabled || !canEditHandicap}
            value={startingHandicap} onChange={(event) => setStartingHandicap(event.target.value)}
            aria-describedby={`starting-handicap-help-${member.player_id}`} />
          <p className="muted" id={`starting-handicap-help-${member.player_id}`}>
            {canEditHandicap
              ? 'Editable until this player records a qualifying round in this league. Absent and non-scoring rounds do not count.'
              : 'Locked after a qualifying round in this league. Handicap history carries across seasons.'}
          </p>
        </div>
        <div className="inline-actions">
          <button type="submit" disabled={disabled}>Save</button>
          <button className="danger" type="button" disabled={disabled} onClick={onRemove}>
            {isSelf ? 'Remove Me' : 'Remove'}
          </button>
        </div>
      </form>
    </div>
  );
}

export function AdminSeasonsPage() {
  const leagueId = useRequiredLeagueId();
  useSyncLeagueFromRoute(leagueId);
  const { client, resetSeasonSelection, setBanner } = useAppState();
  const { invalidate } = useInvalidateQueries();
  const seasonsQuery = useQuery({
    queryKey: ['seasons', leagueId],
    enabled: Boolean(client),
    queryFn: async () => (await client!.getSeasons(leagueId)).seasons,
  });

  const [draft, setDraft] = useState({
    name: '',
    startDate: '',
    endDate: '',
    description: '',
    active: 'false',
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const startDate = toIsoFromDateInput(draft.startDate);
      const endDate = toIsoFromDateInput(draft.endDate);
      if (!startDate || !endDate) {
        throw new Error('Start and end date values are required.');
      }
      return client!.createSeason(leagueId, {
        name: draft.name.trim(),
        startDate,
        endDate,
        active: draft.active === 'true',
        ...(draft.description.trim() ? { description: draft.description.trim() } : {}),
      });
    },
    onSuccess: async () => {
      resetSeasonSelection(leagueId);
      setBanner('success', 'Season created.');
      setDraft({ name: '', startDate: '', endDate: '', description: '', active: 'false' });
      await invalidate(['seasons', leagueId], ['active-season', leagueId]);
    },
    onError: (error: unknown) => setBanner('error', mapError(error)),
  });

  if (seasonsQuery.isLoading) {
    return <LoadingPanel title="Seasons" message="Loading seasons." />;
  }
  if (seasonsQuery.isError || !seasonsQuery.data) {
    return <ErrorPanel title="Seasons" message={mapError(seasonsQuery.error)} />;
  }

  return (
    <>
      <PageIntro
        title="Seasons"
      />
      <section className="grid two">
        <article className="panel">
          <h3>Create Season</h3>
          <form
            className="stack"
            onSubmit={(event) => {
              event.preventDefault();
              void createMutation.mutateAsync();
            }}
          >
            <div className="field">
              <label>Name</label>
              <input
                required
                value={draft.name}
                onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))}
              />
            </div>
            <div className="row">
              <div className="field">
                <label>Start</label>
                <input
                  type="date"
                  required
                  value={draft.startDate}
                  onChange={(event) => setDraft((current) => ({ ...current, startDate: event.target.value }))}
                />
              </div>
              <div className="field">
                <label>End</label>
                <input
                  type="date"
                  required
                  value={draft.endDate}
                  onChange={(event) => setDraft((current) => ({ ...current, endDate: event.target.value }))}
                />
              </div>
            </div>
            <div className="row">
              <div className="field">
                <label>Description</label>
                <input
                  value={draft.description}
                  onChange={(event) => setDraft((current) => ({ ...current, description: event.target.value }))}
                />
              </div>
              <div className="field">
                <label>Active</label>
                <select
                  value={draft.active}
                  onChange={(event) => setDraft((current) => ({ ...current, active: event.target.value }))}
                >
                  <option value="false">No</option>
                  <option value="true">Yes</option>
                </select>
              </div>
            </div>
            <div className="inline-actions">
              <button type="submit" disabled={createMutation.isPending}>
                Create Season
              </button>
            </div>
          </form>
        </article>

        <article className="panel">
          <PanelHeader title={`Season List (${seasonsQuery.data.length})`} />
          {seasonsQuery.data.length === 0 ? (
            <EmptyState title="No seasons configured" message="Create the first season to unlock standings, message board, and match-day workflows." />
          ) : (
            <div className="card-list">
              {seasonsQuery.data.map((season) => (
                <SeasonCard key={season.id} leagueId={leagueId} season={season} />
              ))}
            </div>
          )}
        </article>
      </section>
    </>
  );
}

function SeasonCard({ leagueId, season }: { leagueId: string; season: { id: string; name: string; active: boolean; start_date: string; end_date: string; description?: string } }) {
  const { client, resetSeasonSelection, setBanner } = useAppState();
  const { invalidate } = useInvalidateQueries();
  const [form, setForm] = useState({
    name: season.name,
    active: season.active ? 'true' : 'false',
    startDate: toDateInputValue(season.start_date),
    endDate: toDateInputValue(season.end_date),
    description: season.description || '',
  });

  const updateMutation = useMutation({
    mutationFn: async () => {
      const startDate = toIsoFromDateInput(form.startDate);
      const endDate = toIsoFromDateInput(form.endDate);
      if (!startDate || !endDate) {
        throw new Error('Season date values are invalid.');
      }
      return client!.updateSeason(leagueId, season.id, {
        name: form.name.trim(),
        startDate,
        endDate,
        active: form.active === 'true',
        ...(form.description.trim() ? { description: form.description.trim() } : {}),
      });
    },
    onSuccess: async () => {
      resetSeasonSelection(leagueId);
      setBanner('success', 'Season updated.');
      await invalidate(['seasons', leagueId], ['active-season', leagueId]);
    },
    onError: (error: unknown) => setBanner('error', mapError(error)),
  });

  return (
    <div className="card">
      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          void updateMutation.mutateAsync();
        }}
      >
        <div className="row">
          <div className="field">
            <label>Name</label>
            <input value={form.name} required onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} />
          </div>
          <div className="field">
            <label>Active</label>
            <select value={form.active} onChange={(event) => setForm((current) => ({ ...current, active: event.target.value }))}>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </div>
        </div>
        <div className="row">
          <div className="field">
            <label>Start</label>
            <input type="date" value={form.startDate} required onChange={(event) => setForm((current) => ({ ...current, startDate: event.target.value }))} />
          </div>
          <div className="field">
            <label>End</label>
            <input type="date" value={form.endDate} required onChange={(event) => setForm((current) => ({ ...current, endDate: event.target.value }))} />
          </div>
        </div>
        <div className="field">
          <label>Description</label>
          <input value={form.description} onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))} />
        </div>
        <div className="inline-actions">
          <button type="submit" disabled={updateMutation.isPending}>
            Save
          </button>
          <Link className="nav-link" to={`/leagues/${leagueId}/admin/seasons/${season.id}/players`}>
            Season Players
          </Link>
        </div>
      </form>
    </div>
  );
}

export function AdminSeasonPlayersPage() {
  const leagueId = useRequiredLeagueId();
  const seasonId = useRequiredSeasonId();
  useSyncLeagueFromRoute(leagueId);
  const { client, setBanner } = useAppState();
  const { invalidate } = useInvalidateQueries();
  const membersQuery = useLeagueMembers(leagueId);
  const seasonPlayersQuery = useQuery({
    queryKey: ['seasonPlayers', leagueId, seasonId],
    enabled: Boolean(client),
    queryFn: async () => (await client!.getSeasonPlayers(leagueId, seasonId)).seasonPlayers,
  });
  const seasonsQuery = useQuery({
    queryKey: ['seasons', leagueId],
    enabled: Boolean(client),
    queryFn: async () => (await client!.getSeasons(leagueId)).seasons,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [pendingAdds, setPendingAdds] = useState<Record<string, true>>({});
  const [pendingRemovals, setPendingRemovals] = useState<Record<string, true>>({});
  const workspace = useMemo(
    () =>
      buildSeasonRosterWorkspace({
        members: membersQuery.data ?? [],
        seasonPlayers: seasonPlayersQuery.data ?? [],
        searchTerm,
        pendingAdds,
        pendingRemovals,
      }),
    [membersQuery.data, pendingAdds, pendingRemovals, searchTerm, seasonPlayersQuery.data],
  );
  const hasPendingChanges = workspace.pendingChangeCount > 0;

  const applyMutation = useMutation({
    mutationFn: async () => {
      if (!client) {
        throw new Error('API client unavailable');
      }

      const currentSeasonPlayers = seasonPlayersQuery.data ?? [];
      const activeSeasonPlayerIds = new Set(currentSeasonPlayers.map((seasonPlayer) => seasonPlayer.player_id));
      const removalIds = Object.keys(pendingRemovals).filter((playerId) => activeSeasonPlayerIds.has(playerId));
      const addIds = Object.keys(pendingAdds).filter((playerId) => !activeSeasonPlayerIds.has(playerId));
      let added = 0;
      const updated = 0;
      let removed = 0;

      try {
        for (const playerId of addIds) {
          await client.addSeasonPlayer(leagueId, seasonId, { playerId });
          added += 1;
        }

        for (const playerId of removalIds) {
          await client.removeSeasonPlayer(leagueId, seasonId, playerId);
          removed += 1;
        }
      } catch (error) {
        throw new Error(
          `Season roster save stopped after ${added} add(s), ${updated} update(s), and ${removed} removal(s). ${mapError(error)}`,
        );
      }

      return { added, updated, removed };
    },
    onSuccess: async ({ added, updated, removed }) => {
      clearSeasonRosterDrafts(setPendingAdds, setPendingRemovals);
      setBanner('success', `Season roster saved. Added ${added}, updated ${updated}, removed ${removed}.`);
      await invalidate(['seasonPlayers', leagueId, seasonId]);
    },
    onError: async (error: unknown) => {
      clearSeasonRosterDrafts(setPendingAdds, setPendingRemovals);
      await invalidate(['seasonPlayers', leagueId, seasonId]);
      setBanner('error', mapError(error));
    },
  });

  if (membersQuery.isLoading || seasonPlayersQuery.isLoading || seasonsQuery.isLoading) {
    return <LoadingPanel title="Season Roster" message="Loading season roster." />;
  }
  if (membersQuery.isError || seasonPlayersQuery.isError || seasonsQuery.isError || !membersQuery.data || !seasonPlayersQuery.data) {
    return <ErrorPanel title="Season Roster" message={mapError(membersQuery.error ?? seasonPlayersQuery.error ?? seasonsQuery.error)} />;
  }

  return (
    <SeasonRosterWorkspaceSection
      searchTerm={searchTerm}
      workspace={workspace}
      hasPendingChanges={hasPendingChanges}
      savePending={applyMutation.isPending}
      onSearchTermChange={setSearchTerm}
      onResetChanges={() => clearSeasonRosterDrafts(setPendingAdds, setPendingRemovals)}
      onSaveChanges={() => void applyMutation.mutateAsync()}
      onToggleRemoval={(playerId) =>
        setPendingRemovals((current) => toggleRosterFlag(current, playerId))
      }
      onUndoAdd={(playerId) => {
        setPendingAdds((current) => withoutRosterKey(current, playerId));
      }}
      onAdd={(playerId) =>
        setPendingAdds((current) => ({
          ...current,
          [playerId]: true,
        }))
      }
    />
  );
}

function toggleRosterFlag(current: Record<string, true>, playerId: string): Record<string, true> {
  if (current[playerId]) {
    return withoutRosterKey(current, playerId);
  }
  return {
    ...current,
    [playerId]: true,
  };
}

function withoutRosterKey<T extends Record<string, unknown>>(current: T, playerId: string): T {
  const next = { ...current };
  delete next[playerId];
  return next;
}

function clearSeasonRosterDrafts(
  setPendingAdds: Dispatch<SetStateAction<Record<string, true>>>,
  setPendingRemovals: Dispatch<SetStateAction<Record<string, true>>>,
): void {
  setPendingAdds({});
  setPendingRemovals({});
}
